/** BizOps / DB column limit for address line fields in online journey forms. */
export const ADDRESS_LINE_MAX_LENGTH = 30;

/**
 * Packs up to three address lines so each is at most {@link maxLen} characters.
 * Overflow from line N is prepended to line N+1.
 */
export function packAddressLines(lines, maxLen = ADDRESS_LINE_MAX_LENGTH) {
  const input = [
    String(lines?.[0] ?? "").trim(),
    String(lines?.[1] ?? "").trim(),
    String(lines?.[2] ?? "").trim(),
  ];
  const out = ["", "", ""];
  let overflow = "";

  for (let i = 0; i < 3; i += 1) {
    const combined = `${overflow}${input[i] || ""}`.trim();
    if (combined.length <= maxLen) {
      out[i] = combined;
      overflow = "";
    } else {
      out[i] = combined.slice(0, maxLen);
      overflow = combined.slice(maxLen);
    }
  }

  return out;
}

/** Maps Aadhaar `address` object to three packed lines (house/street, vtc, subdist|dist|loc). */
export function mapAadhaarToAddressLines(addr) {
  if (!addr || typeof addr !== "object") {
    return ["", "", ""];
  }
  const line1 = [addr.house, addr.street].filter(Boolean).join(" ");
  const line2 = String(addr.vtc || "").trim();
  const line3 = String(addr.subdist || addr.dist || addr.loc || "").trim();
  return packAddressLines([line1, line2, line3]);
}

export function packAddressFields(formData, keys) {
  if (!formData || typeof formData !== "object") {
    return formData;
  }
  const [line1, line2, line3] = packAddressLines([
    formData[keys[0]],
    formData[keys[1]],
    formData[keys[2]],
  ]);
  return {
    ...formData,
    [keys[0]]: line1,
    [keys[1]]: line2,
    [keys[2]]: line3,
  };
}

const LIFE_ASSURED_ADDRESS_KEYS = [
  "addressLine1",
  "addressLine2",
  "addressLine3",
];

const COMMUNICATION_ADDRESS_KEYS = [
  "communicationAddressLine1",
  "communicationAddressLine2",
  "communicationAddressLine3",
];

const NOMINEE_MAILING_ADDRESS_KEYS = [
  "mailingAddressLine1",
  "mailingAddressLine2",
  "mailingAddressLine3",
];

const NOMINEE_COMMUNICATION_ADDRESS_KEYS = [
  "communicationAddressLine1",
  "communicationAddressLine2",
  "communicationAddressLine3",
];

const APPOINTEE_ADDRESS_KEYS = [
  "appointeeAddressLine1",
  "appointeeAddressLine2",
  "appointeeAddressLine3",
];

export function normalizeLifeAssuredAddressFields(formData) {
  return packAddressFields(formData, LIFE_ASSURED_ADDRESS_KEYS);
}

export function normalizeCommunicationAddressFields(formData) {
  return packAddressFields(formData, COMMUNICATION_ADDRESS_KEYS);
}

/** Permanent + communication address lines on proposer / life assured forms. */
export function normalizeProposalAddressFields(formData) {
  return normalizeCommunicationAddressFields(
    normalizeLifeAssuredAddressFields(formData),
  );
}

export function normalizeNomineeAddressFields(nominee) {
  if (!nominee || typeof nominee !== "object") {
    return nominee;
  }
  return packAddressFields(
    packAddressFields(
      packAddressFields(nominee, NOMINEE_MAILING_ADDRESS_KEYS),
      NOMINEE_COMMUNICATION_ADDRESS_KEYS,
    ),
    APPOINTEE_ADDRESS_KEYS,
  );
}

export function normalizeNomineesList(nominees) {
  if (!Array.isArray(nominees)) {
    return nominees;
  }
  return nominees.map((nominee) => normalizeNomineeAddressFields(nominee));
}

/**
 * Resolves address lines from persisted journey, Aadhaar, and lead — then packs to max length.
 */
export function resolveAddressLinesFromSources({
  persisted = {},
  aadhaarAddr,
  lead = {},
} = {}) {
  const [aad1, aad2, aad3] = mapAadhaarToAddressLines(aadhaarAddr);
  return packAddressLines([
    String(persisted.addressLine1 || "").trim() || aad1 || lead.addressLine1 || "",
    String(persisted.addressLine2 || "").trim() || aad2 || lead.addressLine2 || "",
    String(persisted.addressLine3 || "").trim() || aad3 || lead.addressLine3 || "",
  ]);
}
